import React, { useEffect, useState } from "react";
import {
  Card, CardContent, Typography, Grid, Avatar, Chip,
  Box, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Snackbar, Alert, IconButton, Fab, Divider, Switch, FormControlLabel, ToggleButtonGroup, ToggleButton, InputLabel, Select, MenuItem, FormControl, Autocomplete
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import axios from "axios";
import { API_ENDPOINTS, getUploadUrl } from "config/api";
import { decryptText } from "config/encryption";
import confetti from "canvas-confetti";
import MDButton from "components/MDButton";
import Pagination from '@mui/material/Pagination';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CareerComments from "./CareerComments";
import StudentFullProfile from "./StudentFullProfile";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Tooltip from '@mui/material/Tooltip';

const CareerComponent = () => {
  const [careers, setCareers] = useState([]);
  const [form, setForm] = useState({
    careerTitle: "",
    careerDescription: "",
    isExpired: false,
    jobType: 2,
  });
  const [studentId, setStudentId] = useState(null);
  const [token, setToken] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [openDialog, setOpenDialog] = useState(false);
  const [editId, setEditId] = useState(0);
  const [selectedJobType, setSelectedJobType] = useState(2);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(3);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [selectedCareerId, setSelectedCareerId] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileStudentId, setProfileStudentId] = useState(null);

  const jobTypeOptions = [
    { value: 2, label: "Need Jobs" },
    { value: 1, label: "Hiring for Jobs" },
  ];

  const fetchCareerData = async (authToken, jobType) => {
    try {
      const response = await axios.post(
        API_ENDPOINTS.PROFILE.GET_CAREER_RECORDS,
        { jobType },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      setCareers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Failed to fetch career records:", err.message);
    }
  };

  useEffect(() => {
    const encryptedData = localStorage.getItem("UserLogDetails");
    if (!encryptedData) return;
    const decryptedData = decryptText(encryptedData);
    if (!decryptedData) return;
    const { Token, StudentId } = JSON.parse(decryptedData);
    setStudentId(StudentId);
    setToken(Token);
  }, []);

  useEffect(() => {
    if (token) {
      fetchCareerData(token, selectedJobType);
    }
  }, [token, selectedJobType]);

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleJobTypeChange = (event, newValue) => {
    if (newValue) {
      setSelectedJobType(newValue);
      setPage(1);
    }
  };

  const handleCareerSubmit = async () => {
    try {
      const payload = {
        id: editId || 0,
        studentId,
        careerTitle: form.careerTitle,
        careerDescription: form.careerDescription,
        postedDate: new Date().toISOString(),
        isExpired: form.isExpired,
        jobType: form.jobType,
      };

      const res = await axios.post(
        API_ENDPOINTS.PROFILE.MERGE_CAREER_RECORD,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data?.message) {
        setSnackbar({ open: true, message: res.data.message, severity: "success" });
        confetti({ particleCount: 150, spread: 90, origin: { y: 0.6 } });
        setForm({ careerTitle: "", careerDescription: "", isExpired: false, jobType: 2 });
        setEditId(0);
        setOpenDialog(false);
        fetchCareerData(token, selectedJobType);
      }
    } catch (err) {
      console.error("Failed to post career:", err);
      setSnackbar({ open: true, message: "Failed to submit.", severity: "error" });
    }
  };

  const openEditDialog = (career) => {
    setForm({
      careerTitle: career.careerTitle,
      careerDescription: career.careerDescription,
      isExpired: career.isExpired,
      jobType: career.jobType,
    });
    setEditId(career.id);
    setOpenDialog(true);
  };

  const handleDetailsOpen = (career) => {
    setSelectedCareer(career);
    setDetailsOpen(true);
  };

  const totalCount = careers.length;
  const paginatedCareers = careers.slice((page - 1) * pageSize, page * pageSize);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3, background: '#f7f9fb', minHeight: '100vh' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h6" fontWeight={500}>Career Opportunities</Typography>
        {/* Add filter/search bar here */}
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
 
        <Box display="flex" alignItems="center" gap={2}>
          <Fab
            color="primary"
            size="small"
            onClick={() => {
              setForm({ careerTitle: "", careerDescription: "", isExpired: false, jobType: selectedJobType });
              setEditId(0);
              setOpenDialog(true);
            }}
            aria-label={selectedJobType === 2 ? "Post a Job" : "I'm Available for Work"}
            title={selectedJobType === 2 ? "Post a Job" : "I'm Available for Work"}
          >
            <AddIcon />
          </Fab>
          <ToggleButtonGroup
            value={selectedJobType}
            exclusive
            onChange={(e, newValue) => { if (newValue) setSelectedJobType(newValue); }}
            sx={{ ml: 2 }}
          >
            <ToggleButton value={2}>Need Jobs</ToggleButton>
            <ToggleButton value={1}>Hiring for Jobs</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        {paginatedCareers.map((career) => {
          return (
            <Grid item xs={12} sm={6} md={4} key={career.id}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: 4,
                  borderLeft: `5px solid ${career.jobType === 2 ? '#1976d2' : '#43a047'}`,
                  background: "linear-gradient(135deg, #f7f9fb 80%, #e3f2fd 100%)",
                  transition: "box-shadow 0.2s, transform 0.2s",
                  '&:hover': { boxShadow: 8, transform: 'translateY(-4px) scale(1.02)' },
                  p: 1.5,
                  minHeight: 200,
                  maxHeight: 200,
                  height: 200,
                  minWidth: 220,
                  maxWidth: 320,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  position: 'relative',
                }}
              >
                {career.studentId === studentId && (
                  <IconButton
                    size="medium"
                    sx={{ 
                      position: "absolute", 
                      top: 8, 
                      right: 8, 
                      zIndex: 3,
                      background: '#e3f2fd',
                      color: '#1976d2',
                      boxShadow: 1,
                      '&:hover': { background: '#bbdefb' },
                      width: 36,
                      height: 36,
                      p: 0.75,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditDialog(career);
                    }}
                  >
                    <EditIcon fontSize="medium" />
                  </IconButton>
                )}
                <Box display="flex" alignItems="center" gap={1.5} position="relative">
                  <Avatar
                    src={getUploadUrl(career.img)}
                    sx={{ width: 36, height: 36, border: '2px solid #eee', boxShadow: 1 }}
                  >
                    {career.fullName ? career.fullName[0] : "?"}
                  </Avatar>
                  <Box>
                    <Typography fontWeight={700} fontSize={15} sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{career.careerTitle}</Typography>
                    <Typography fontSize={11} color="text.secondary" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{career.fullName}</Typography>
                  </Box>
                </Box>
                {/* Chips row: jobTypeLabel and isExpired */}
                <Box display="flex" alignItems="center" gap={1} mb={0.5} mt={0.5}>
                  <Chip label={career.jobTypeLabel} color={career.jobType === 2 ? "primary" : "success"} size="small" sx={{ fontWeight: 600, height: 22, fontSize: 11 }} />
                  <Chip label={career.isExpired ? "Expired" : "Active"} color={career.isExpired ? "error" : "success"} size="small" sx={{ height: 20, fontSize: 10 }} />
                </Box>
                <Typography fontSize={12} color="text.secondary" sx={{ mt: 0.5, mb: 0.5, whiteSpace: 'pre-line', maxHeight: 32, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {career.careerDescription}
                </Typography>
              
                <Divider sx={{ my: 0.5 }} />
                <Box display="flex" alignItems="center" gap={1.5} sx={{ mt: 'auto' }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title="View Comments">
                      <IconButton size="small" sx={{ color: '#1976d2' }} onClick={() => { setSelectedCareerId(career.id); setCommentsOpen(true); }}>
                        <ChatBubbleOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary">
                      {career.commentCount !== undefined ? career.commentCount : '*'}
                    </Typography>
                  </Box>
                  <Tooltip title="View Profile">
                    <IconButton size="small" sx={{ color: '#1976d2' }} onClick={() => { setProfileStudentId(career.studentId); setProfileOpen(true); }}>
                      <AccountCircleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <MDButton size="small" variant="outlined" color="info" sx={{ ml: 'auto', fontWeight: 600, height: 26, fontSize: 12 }} onClick={() => handleDetailsOpen(career)}>Details</MDButton>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          count={Math.ceil(totalCount / pageSize)}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
        />
      </Box>

      {/* Dialog for Create/Edit */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? "Edit Career" : "Create New Career"}</DialogTitle>
        <DialogContent>
          <TextField
            name="careerTitle"
            label="Career Title"
            fullWidth
            value={form.careerTitle}
            onChange={handleInputChange}
            sx={{ my: 1 }}
          />
          <TextField
            name="careerDescription"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={form.careerDescription}
            onChange={handleInputChange}
            sx={{ my: 1 }}
          />
          <Chip
            label={jobTypeOptions.find(opt => opt.value === form.jobType)?.label || ""}
            color={form.jobType === 2 ? "primary" : "success"}
            sx={{ my: 1, fontWeight: 600 }}
          />
          <FormControlLabel
            control={
              <Switch
                name="isExpired"
                checked={form.isExpired}
                onChange={handleInputChange}
              />
            }
            label={form.isExpired ? "Expired" : "Active"}
            sx={{ my: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <MDButton variant="contained" onClick={handleCareerSubmit}>
            {editId ? "Update" : "Save"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontSize: 18 }}>{selectedJobType === 2 ? "Job Details" : "Person Details"}</DialogTitle>
        <DialogContent>
          {selectedCareer && (
            <>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar
                  src={getUploadUrl(selectedCareer.img)}
                  sx={{ width: 48, height: 48, border: '2px solid #eee' }}
                >
                  {selectedCareer.fullName ? selectedCareer.fullName[0] : "?"}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ fontSize: 15 }}>{selectedCareer.careerTitle}</Typography>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: 12 }}>
                    {selectedJobType === 2 ? `Posted by: ${selectedCareer.fullName}` : `Person: ${selectedCareer.fullName}`}
                  </Typography>
                  <Chip
                    label={selectedCareer.jobTypeLabel}
                    color={selectedCareer.jobType === 2 ? "primary" : "success"}
                    sx={{ mt: 1, fontWeight: 600, fontSize: 12, height: 22 }}
                  />
                </Box>
              </Box>
              <Typography sx={{ mb: 2, fontSize: 13, whiteSpace: 'pre-line' }} color="text.primary">{selectedCareer.careerDescription}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: 11 }}>
                Posted On: {new Date(selectedCareer.postedDate).toLocaleString()}
              </Typography>
              <Chip
                label={selectedCareer.isExpired ? "Expired" : "Active"}
                color={selectedCareer.isExpired ? "error" : "success"}
                size="small"
                sx={{ mt: 1, fontWeight: 500, fontSize: 11, height: 20 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)} sx={{ fontSize: 12, minWidth: 60, height: 28 }}>Close</Button>
        </DialogActions>
      </Dialog>

      <CareerComments
        careerId={selectedCareerId}
        open={commentsOpen}
        onClose={() => setCommentsOpen(false)}
      />

      {/* Profile View Dialog */}
      <Dialog open={profileOpen} onClose={() => setProfileOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontSize: 18 }}>Alumni Profile</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {profileStudentId && token && (
            <StudentFullProfile studentId={profileStudentId} token={token} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileOpen(false)} sx={{ fontSize: 12, minWidth: 60, height: 28 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CareerComponent;
